import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error monitoring.
 *
 * Sentry only activates when VITE_SENTRY_DSN is set (in Cloudflare Pages
 * env vars or local .env). If the DSN is missing, all Sentry APIs become
 * no-ops — no network calls, no console noise, no perf hit. This lets us
 * ship the SDK without forcing every dev to set up Sentry locally.
 *
 * To enable in production:
 *   1. Sign up at sentry.io and create a React project
 *   2. Copy the DSN (looks like https://abc@sentry.io/123)
 *   3. Add VITE_SENTRY_DSN to Cloudflare Pages env vars
 *   4. Redeploy
 *
 * Why client-side only: bouqueh.com is a static SPA on Cloudflare Pages.
 * The bot prerender worker runs server-side but has its own error
 * handling. Edge functions in Supabase have their own logging. So
 * Sentry's job here is browser runtime errors.
 *
 * What gets captured:
 *   - Unhandled exceptions (any uncaught throw or promise rejection)
 *   - Errors caught by ErrorBoundary (see SentryErrorBoundary export)
 *   - React lifecycle errors (rendering, effects)
 *   - Errors explicitly reported via Sentry.captureException()
 *
 * What does NOT get captured (by design):
 *   - PII — we strip URL params and don't capture user identifiers
 *   - Card/payment data — never sent to browser anyway
 *   - DOM content — would leak addresses/names from order forms
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    // Silently no-op when DSN is missing. Logged only in dev so devs know.
    if (import.meta.env.DEV) {
      console.info('[Sentry] VITE_SENTRY_DSN not set — error monitoring disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE || 'unknown',

    // Sample rates — adjust if quota becomes an issue
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    replaysSessionSampleRate: 0, // disabled — privacy + bandwidth
    replaysOnErrorSampleRate: 0, // disabled — privacy

    // Privacy guard — strip URL query params that might include user data
    beforeSend(event) {
      try {
        if (event.request?.url) {
          const u = new URL(event.request.url);
          // Drop all query params — order tokens, search queries, etc.
          u.search = '';
          event.request.url = u.toString();
        }
        // Never include user IP / email — Sentry's IP-collection is off by default
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
        }
      } catch {
        // If URL parsing fails, drop the URL entirely rather than risk a leak
        if (event.request) event.request.url = undefined;
      }
      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Don't capture errors from browser extensions or third-party scripts
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Network errors are usually user-side (offline, captive portal) — noise
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Browser extension noise
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
    ],
  });
};

/**
 * Wrap a React tree in a Sentry ErrorBoundary. When the boundary catches
 * an error, Sentry receives it AND the user sees a friendly fallback UI.
 *
 * Falls back to a plain ErrorBoundary if Sentry isn't initialized.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Manually report an error from inside a try/catch or onError handler.
 * No-op if Sentry isn't initialized.
 */
export const captureException = Sentry.captureException;
